import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Image from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {colors, fontSize, fontWeight, borderRadius, spacing, lineHeightRatio} from '@/styles/tokens';
import {cdnUrl} from '@/util/cdnImage';
import {renderParts} from './richText';
import type {outcomeArchContent} from '@/util/contents/drawpost-data';

interface OutcomeArchitectureProps {
    heading: string;
    content: typeof outcomeArchContent;
}

const sectionSx = {
    bgcolor: colors.background.subtle
} as const;

const textColSx = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
} as const;

const badgeSx = {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    alignItems: 'center',
    gap: spacing[8],
    px: spacing[16],
    py: spacing[8],
    borderRadius: borderRadius.full,
    bgcolor: colors.accent.indigoSubtle,
    color: colors.accent.indigo,
    fontSize: fontSize[16],
    fontWeight: fontWeight.semibold,
    mb: 1
} as const;

const badgeIconFilter =
    'brightness(0) saturate(100%) invert(20%) sepia(95%) saturate(6000%) hue-rotate(244deg) brightness(95%) contrast(101%)';

const headingSx = {
    color: colors.text.primary,
    fontSize: {xs: fontSize[32], sm: fontSize[48]},
    lineHeight: lineHeightRatio.snug,
    mb: 3
} as const;

const bodyTextSx = {
    color: colors.text.primary,
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeightRatio.looser,
    mb: 3
} as const;

const quoteBoxSx = {
    bgcolor: colors.background.tag,
    borderRadius: borderRadius['2xl'],
    px: {xs: 3, md: 4},
    py: {xs: 3, md: 4}
} as const;

const quoteTextSx = {
    color: colors.text.primary,
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeightRatio.looser
} as const;

const screenshotSx = {
    overflow: 'hidden',
    borderRadius: borderRadius.xl,
    width: '100%',
    mx: 'auto'
} as const;

const screenshotImageStyle = {width: '100%', height: 'auto', display: 'block'} as const;

const richTextBoldSx = {fontWeight: fontWeight.semibold, color: colors.text.primary} as const;
const richTextLinkStyle = {fontWeight: fontWeight.semibold, color: colors.primary.main, textDecoration: 'underline'} as const;

const OutcomeArchitecture = memo(function OutcomeArchitecture({heading, content}: OutcomeArchitectureProps) {
    return (
        <Box className="top-space bottom-spacing mobile-top-padding mobile-padding-bottom" sx={sectionSx}>
            <MUIContainer>
                <Grid container spacing={{xs: 4, md: 8}} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Box sx={screenshotSx}>
                            <Image
                                src={cdnUrl(content.screenshotPath)}
                                alt={content.screenshotAlt}
                                width={1200}
                                height={800}
                                loading="lazy"
                                style={screenshotImageStyle}
                                sizes="(max-width: 900px) 100vw, 50vw"
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6} sx={textColSx}>
                        <Box sx={badgeSx}>
                            <Image
                                src={cdnUrl(content.badgeIconSrc)}
                                alt={content.badgeIconAlt}
                                aria-hidden="true"
                                width={16}
                                height={16}
                                style={{filter: badgeIconFilter}}
                            />
                            <span>{content.badgeLabel}</span>
                        </Box>

                        <Typography component="h2" sx={headingSx} className="font-headers">
                            {heading}
                        </Typography>

                        <Typography sx={bodyTextSx} className="font-regular-text remove-underline">
                            {renderParts(content.body, {boldSx: richTextBoldSx, linkStyle: richTextLinkStyle})}
                        </Typography>

                        <Box sx={quoteBoxSx}>
                            <Typography sx={quoteTextSx} className="font-regular-text remove-underline">
                                {renderParts(content.quote, {boldSx: richTextBoldSx, linkStyle: richTextLinkStyle})}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
});

export default OutcomeArchitecture;
